import React, { useState, useMemo } from "react";
import { Box } from "@mui/material";
import styles from "./destinationOriginPanelMobile.module.css";
import { OriginTableMobile } from "./components/originTableMobile";
import RailIconFO from "../../icons/RailIconFO";
import TruckIconFO from "../../icons/TruckIconFO";
import { icons } from "../../svg";
import { IQuotesByDestination } from "../../types/IQuotes";
import { NitroMarkerBase } from "../../components/map/components/NitroMarker";
import { useNitroStore } from "../../store/Store";
import { resolveNetbackForSort } from "../../utils/utils";
import { resolveKochDlvdModeFromDestination } from "../mobile/multiLocQuoteDetailsPanel/multiLocQuoteDetailsUtils";

const SingleDestinationPanel: React.FC<{
  destinationQuote: IQuotesByDestination;
  party?: string | null;
}> = ({ destinationQuote, party }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { destination, quotes } = destinationQuote;

  const [product, favoriteOrigins] = useNitroStore((state) => [
    state.product,
    state.favoriteOrigins,
  ]);

  const { kochOrigins, competitorOrigins } = useMemo(() => {
    const normalizedProduct = (product || "AMMONIA").toUpperCase();
    const originsForProduct =
      favoriteOrigins.find((o) => o.product.toUpperCase() === normalizedProduct)
        ?.favoriteLocations || [];

    const withEnabledFlags = originsForProduct.map((favorite) => {
      const outboundModes = favorite.location.outboundModes || [];
      return {
        ...favorite,
        truckEnabled: outboundModes.some((m) => m === "TRUCK" || m === "TRUNK"),
        railEnabled: outboundModes.includes("RAIL"),
      };
    });

    return {
      kochOrigins: withEnabledFlags.filter((f) => !f.competitor),
      competitorOrigins: withEnabledFlags.filter((f) => f.competitor),
    };
  }, [favoriteOrigins, product]);

  const displayableQuotes = useMemo(() => {
    return quotes.filter((quote) => {
      const originsList = quote.competitor ? competitorOrigins : kochOrigins;
      const favoriteOrigin = originsList.find(
        (fav) => fav.location.id === quote.origin.id,
      );

      if (!favoriteOrigin) {
        return true;
      }

      const mode = quote.mode.toUpperCase();
      const outboundModes = favoriteOrigin.location.outboundModes || [];
      return outboundModes.some(
        (outboundMode) =>
          outboundMode.toUpperCase() === mode ||
          (mode === "TRUCK" && outboundMode.toUpperCase() === "TRUNK"),
      );
    });
  }, [quotes, kochOrigins, competitorOrigins]);

  const formatPrice = (price: number | null | undefined) => {
    if (price == null) return "$--";
    return `$${price.toFixed(2)}`;
  };

  const hasDisplayableQuotes = displayableQuotes.length > 0;

  // same postedPrice/truckPrice/railPrice resolution pattern used in originTableMobile.tsx
  const kochDlvdMode = resolveKochDlvdModeFromDestination(destination);

  const bestOffer = displayableQuotes
    .map((quote) => ({
      quote,
      netback: resolveNetbackForSort(quote as any, kochDlvdMode),
    }))
    .filter((entry) => entry.netback != null)
    .sort(
      (a, b) => (a.netback ?? Infinity) - (b.netback ?? Infinity),
    )[0]?.quote; // Sort ascending, take first

  const fobNetback = bestOffer
    ? resolveNetbackForSort(bestOffer as any, kochDlvdMode)
    : null;
  const altNetback = bestOffer
    ? resolveNetbackForSort(bestOffer as any, { truck: "alt", rail: "alt" })
    : null;
  const freight = bestOffer?.freight?.totalFreight ?? null;
  const dlvdOffer =
    fobNetback != null && freight != null ? fobNetback + freight : null;
  const originName = bestOffer?.origin.name ?? "--";
  const mode = bestOffer?.mode;

  let netbackRange: string | null = null;
  if (fobNetback != null) {
    netbackRange =
      altNetback != null && altNetback !== fobNetback
        ? `($${fobNetback}-$${altNetback})`
        : `($${fobNetback}-$${fobNetback})`;
  }

  const toggleView = () => {
    setIsExpanded(!isExpanded);
  };

  const destinationDisplayName =
    party && destination.name.includes("-")
      ? `${destination.city}, ${destination.stateOrProvince}`
      : destination.name;

  return (
    <div className={styles.panelContainer}>
      <div className={styles.destinationHeader}>
        <Box className={styles.locationCell}>
          <NitroMarkerBase width={12} type={destination.pinType || ""} />
          <span className={styles.destinationLocation}>
            {destinationDisplayName}
          </span>
        </Box>
      </div>

      <div className={styles.offerCard}>
        <div className={styles.offerHeader}>
          <div className={styles.rowHeaderWrapper}>
            <div className={styles.rowHeader}>
              {hasDisplayableQuotes && mode?.toUpperCase() === "RAIL" && (
                <RailIconFO color="#F97316" />
              )}
              {hasDisplayableQuotes && mode?.toUpperCase() === "TRUCK" && (
                <TruckIconFO color="#60A5FA" />
              )}
              <span id="offer-card-origin-name" className={styles.originName}>
                {originName}
              </span>
            </div>
            {hasDisplayableQuotes &&
              mode?.toUpperCase() === "RAIL" &&
              bestOffer?.freight != null &&
              "minCars" in bestOffer.freight &&
              bestOffer.freight.minCars != null && (
                <span id="rail-car-info" className={styles.railCarInfo}>
                  {bestOffer.freight.minCars === 1
                    ? "SINGLE"
                    : `${bestOffer.freight.minCars}+ UT`}
                </span>
              )}
          </div>
        </div>

        <div className={styles.offerDetails}>
          <div className={`${styles.offerColumn} ${styles.dlvdColumn}`}>
            <span className={styles.detailLabel}>DLVD Offer</span>
            <div className={styles.detailValue}>{formatPrice(dlvdOffer)}</div>
          </div>
          <div className={styles.offerColumn}>
            <span className={styles.detailLabel}>Freight</span>
            <div className={styles.detailValueFreight}>
              {formatPrice(freight)}
            </div>
          </div>
          <div className={styles.offerColumn}>
            <span className={styles.detailLabel}>FOB Netback</span>
            <div className={styles.netbackValue}>{formatPrice(fobNetback)}</div>
            {netbackRange && (
              <span className={styles.detailSubtext}>{netbackRange}</span>
            )}
          </div>
        </div>
      </div>

      <div
        className={styles.toggleRow}
        onClick={hasDisplayableQuotes ? toggleView : undefined}
        role="button"
        tabIndex={hasDisplayableQuotes ? 0 : -1}
        onKeyDown={(e) => {
          if (hasDisplayableQuotes && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            toggleView();
          }
        }}
        aria-expanded={isExpanded}
        aria-disabled={!hasDisplayableQuotes}
        style={{
          opacity: hasDisplayableQuotes ? 1 : 0.5,
          cursor: hasDisplayableQuotes ? "pointer" : "not-allowed",
        }}
      >
        {isExpanded ? (
          <>
            <img
              data-tooltip-id="downward-expand-icon"
              src={icons.DownwardExpandIcon}
              alt="Downward Expand Icon"
              style={{ transform: "rotate(180deg)" }}
            />
            <span>Hide All Origins</span>
          </>
        ) : (
          <>
            <img
              data-tooltip-id="downward-expand-icon"
              src={icons.DownwardExpandIcon}
              alt="Downward Expand Icon"
            />
            <span>View All Origins</span>
          </>
        )}
      </div>

      {isExpanded && hasDisplayableQuotes && (
        <OriginTableMobile
          quotes={displayableQuotes}
          destination={destination}
        />
      )}
    </div>
  );
};

export const DestinationOriginPanelMobile: React.FC<{
  destinationQuotes: IQuotesByDestination[];
  party?: string | null;
}> = ({ destinationQuotes, party }) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredQuotes = useMemo(() => {
    if (!searchTerm.trim()) {
      return destinationQuotes;
    }
    return destinationQuotes.filter((dq) =>
      dq.destination.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [destinationQuotes, searchTerm]);

  return (
    <div className={styles.listWrapper}>
      {party && (
        <div className={styles.searchBarContainer}>
          <div className={styles.searchInputWrapper}>
            <img
              src={icons.SearchMagnifyingGlass}
              alt="Search Magnifying Glass Icon"
              className={styles.searchIcon}
            />
            <input
              type="text"
              placeholder="Filter destination …"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>
      )}
      {filteredQuotes.map((dq) => (
        <SingleDestinationPanel
          key={dq.destination.id}
          destinationQuote={dq}
          party={party}
        />
      ))}
    </div>
  );
};
